import { isString } from 'lodash-es';
import MediaInfoFactory, {
  type MediaInfo,
  type MediaInfoResult,
} from 'mediainfo.js';

import { allowedImageProxyFormatsSet } from '@iconik-io/shared-lib/src/utils/media_type';

let mediaInfo: MediaInfo<'object'> | Promise<MediaInfo<'object'>> | undefined;

const readChunk =
  (file: File) =>
  (chunkSize: number, offset: number): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event?.target?.error) {
          reject(event.target.error);
        }
        if (event.target?.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(event.target.result));
        } else {
          reject();
        }
      };
      reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
    });

export const readFile = async (
  file: File,
): Promise<MediaInfoResult | undefined> => {
  if (mediaInfo === undefined) {
    mediaInfo = MediaInfoFactory({
      locateFile: () => '/wasm/MediaInfoModule.wasm',
    });
  }

  const newResult = await (await mediaInfo)
    .analyzeData(() => file.size, readChunk(file))
    .catch((error) => {
      // biome-ignore lint/suspicious/noConsole: mediaInfo.js errors logging
      console.error(error);
      return undefined;
    });
  return newResult;
};

export const isVideo = (data: MediaInfoResult): boolean => {
  if (data && !isString(data) && data.media?.track[0]['@type'] === 'General') {
    return data.media.track[0].Format === 'MPEG-4';
  }
  return false;
};

export const isImage = (data: MediaInfoResult): boolean =>
  Boolean(
    data &&
      !isString(data) &&
      data.media?.track.length === 2 &&
      data.media.track[1]['@type'] === 'Image',
  );

export const isAudio = (data: MediaInfoResult): boolean => {
  if (data && !isString(data) && data.media?.track[0]['@type'] === 'General') {
    const general = data.media.track[0];
    return general.Format === 'MPEG Audio';
  }
  return false;
};

export const validateImage = (data: MediaInfoResult): string[] => {
  if (!data || isString(data)) {
    return ["Can't read file"];
  }
  const format = data.media?.track?.[0]?.Format;
  if (!isString(format)) {
    return ["Can't read file"];
  }
  if (!allowedImageProxyFormatsSet.has(format.toUpperCase())) {
    return [
      `Image must be one of: ${[...allowedImageProxyFormatsSet].join(', ')}`,
    ];
  }

  return [];
};

export const validateAudio = (data: MediaInfoResult): string[] => {
  const errors: string[] = [];
  if (data && !isString(data)) {
    const tracks = data.media?.track ?? [];
    const audioTracks = tracks.filter((track) => track['@type'] === 'Audio');
    if (audioTracks.length > 1) {
      errors.push('File can only contain one audio track');
    }
  } else {
    errors.push("Can't read file");
  }
  return errors;
};

export const validateVideo = (
  data: MediaInfoResult,
  type: string,
): string[] => {
  const errors: string[] = [];
  if (data && !isString(data)) {
    const tracks = data.media?.track ?? [];
    const audioTracks = tracks.filter((track) => track['@type'] === 'Audio');
    const videoTracks = tracks.filter((track) => track['@type'] === 'Video');
    if (type !== 'video/mp4') {
      errors.push('File must be mp4');
    }
    if (audioTracks.length > 1) {
      errors.push('File can only contain one audio track');
    }
    if (
      audioTracks.length &&
      audioTracks.find(
        (track) => track['@type'] === 'Audio' && track.Format !== 'AAC',
      )
    ) {
      errors.push('Audio codec must be AAC');
    }
    if (
      videoTracks.length &&
      videoTracks.find((track) => track.Format !== 'AVC')
    ) {
      errors.push('Wrong encoding');
    }
  } else {
    errors.push("Can't read file");
  }
  return errors;
};

export const validate = (data: MediaInfoResult, type: string): string[] => {
  if (isImage(data)) {
    return validateImage(data);
  }
  if (isAudio(data)) {
    return validateAudio(data);
  }
  if (isVideo(data)) {
    return validateVideo(data, type);
  }
  return ['Wrong format'];
};
